
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { DemoBookingModal } from "@/components/DemoBookingModal";

interface ProductPageTemplateProps {
  title: string;
  description: string;
  keywords: string;
  heroTitle: string;
  heroDescription: string;
  featureDescription: string;
  benefits: {
    title: string;
    description: string;
    icon: React.ReactNode;
  }[];
  howItWorks: {
    title: string;
    description: string;
    image: string;
  }[];
  ctaText: string;
  ctaButtonText: string;
  mainImage: string;
}

const ProductPageTemplate: React.FC<ProductPageTemplateProps> = ({
  title,
  description,
  keywords,
  heroTitle,
  heroDescription,
  featureDescription,
  benefits,
  howItWorks,
  ctaText,
  ctaButtonText,
  mainImage
}) => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>{title} | Assembly - Employee Recognition Platform</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Assembly" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        {typeof window !== 'undefined' && <link rel="canonical" href={window.location.href} />}
      </Helmet>
      
      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="bg-gradient-to-b from-blue-50 to-white py-20 px-4 pt-16">
            <div className="container mx-auto max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="lg:pr-12">
                  <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                    {heroTitle}
                  </h1>
                  <p className="text-xl text-gray-600 mb-8">
                    {heroDescription}
                  </p>
                  <Button 
                    onClick={() => setIsDemoModalOpen(true)}
                    className="bg-assembly-orange hover:bg-orange-700 text-white px-8 py-6 text-lg rounded-lg font-bold"
                  >
                    {ctaButtonText}
                  </Button>
                </div>
                <div className="relative">
                  <img
                    src={mainImage}
                    alt={title}
                    className="w-full h-auto rounded-lg shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </section>
          
          {/* Feature Description Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto max-w-4xl text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is {title}?
              </h2>
              <p className="text-xl text-gray-600">
                {featureDescription}
              </p>
            </div>
          </section>
          
          {/* Benefits Section */}
          <section className="py-20 px-4 bg-gray-50">
            <div className="container mx-auto max-w-7xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                Key Benefits
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-md transition-transform hover:scale-105">
                    <div className="text-assembly-orange mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* How It Works Section */}
          <section className="py-20 px-4">
            <div className="container mx-auto max-w-7xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                How It Works
              </h2>
              <div className="space-y-16">
                {howItWorks.map((step, index) => (
                  <div 
                    key={index} 
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                      index % 2 === 1 ? "lg:flex-row-reverse" : ""
                    }`}
                  >
                    <div className={index % 2 === 1 ? "lg:pl-12" : "lg:pr-12"}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </h3>
                      <p className="text-gray-600">
                        {step.description}
                      </p>
                    </div>
                    <div className="relative">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-auto rounded-lg shadow-xl"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            <div className="container mx-auto max-w-4xl text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {ctaText}
              </h2>
              <Button 
                onClick={() => setIsDemoModalOpen(true)}
                className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-bold"
              >
                {ctaButtonText}
              </Button>
            </div>
          </section>
        </main>
        
        <Footer />
        
        <DemoBookingModal 
          open={isDemoModalOpen} 
          onOpenChange={setIsDemoModalOpen} 
        />
      </div>
    </>
  );
};

export default ProductPageTemplate;
